import React from 'react';
import { string, shape, func, number } from 'prop-types';
import DebugBundleItem from 'components/DebugBundleItem';

export const DEFAULT_COMPONENT = DebugBundleItem;

/**
 * this is abstract component that accepts an `item` and a `components`
 * mapping, which specifies which component to use based on the item's type.
 * components is a simple object/hash map with the key as the type and the
 * value as the actual component function/class. if the type is not found in
 * `components`, a default (debug) component is rendered in development mode
 * only; nothing is rendered for an unmapped type item type in production.
 *
 * two optional functions may be passed:
 *
 * `customComponentMapper` is a function that will return a component when it
 * is provided the item, variant, components, idx, totalItems, and
 * defaultComponent. this allows custom logic to be used to to select the
 * appropriate component. note that this function MUST return a component,
 * which is why the defaultComponent is provided. the custom function may
 * choose to use this or implement its own default component.
 *
 * `customPropsMapper` is a function that will return a component's props when
 * it is provided the item, type, variant, idx, and totalItems. this allows
 * you to massage the shape/values of the data passed to the component. for
 * example, you may want to adjust the data based on `variant`, total number
 * of items, or other pieces of data. as with the customComponentMapper, props
 * MUST be returned or nothing will be passed to the component.
 *
 * when using custom mapping function, you will usually want to pass in three
 * additional props to this component: `variant` (the variant of the bundle
 * this item component is being used in), `idx` (the current zero-based index
 * of the item within the array of bundle items), and `totalItems` (the total
 * number of bundle items).
 */
function BundleItemComponent(props) {
  const {
    item,
    variant,
    customPropsMapper,
    customComponentMapper,
    idx,
    totalItems,
    components,
    ...otherProps
  } = props;
  const { data, type } = item;

  // do a default mapping here to make the next statement easier to parse.
  const mappedComponent = components[type]
    ? components[type]
    : DEFAULT_COMPONENT;

  // compute the type of component if a custom function is provided.
  const ItemComponent =
    typeof customComponentMapper === 'function'
      ? customComponentMapper(
          item,
          variant,
          components,
          idx,
          totalItems,
          DEFAULT_COMPONENT
        )
      : mappedComponent;

  // PROP MAPPING: STEP 1
  //
  // allow for defining an optional 'mapProps' static method on the component
  // for customizing the 'default' mapping of the props (the item's data). note
  // that we're passing `item` to mapProps so that it can use that if necessary
  // to compute values.
  const defaultItemProps =
    typeof ItemComponent.mapProps === 'function'
      ? ItemComponent.mapProps(item)
      : data;

  // PROP MAPPING: STEP 2
  //
  // compute the bundle item props if a custom function is provided. note
  // that the customPropsMapper gets the data AFTER any massaged by mapProps().
  const itemProps =
    typeof customPropsMapper === 'function'
      ? customPropsMapper(defaultItemProps, type, variant, idx, totalItems)
      : defaultItemProps;

  return <ItemComponent {...itemProps} {...otherProps} />;
}

BundleItemComponent.propTypes = {
  item: shape({
    id: string.isRequired,
    type: string.isRequired,
    data: shape({
      // arbitrary per component type
    }).isRequired
  }).isRequired,
  variant: string,
  components: shape({}).isRequired,
  customPropsMapper: func,
  customComponentMapper: func,
  idx: number,
  totalItems: number
};

BundleItemComponent.defaultProps = {
  variant: undefined,
  customPropsMapper: undefined,
  customComponentMapper: undefined,
  idx: undefined,
  totalItems: undefined
};

export default BundleItemComponent;
